/**
 * story-animations.js
 * Blur-reveal animations — two targets, loaded site-wide:
 *
 *   1. Rich text section headings (.index-rte h1/h2/h3)
 *      → ScrollTrigger: reveals as each heading enters the viewport.
 *
 *   2. Hero video section titles (.gsap-video-title)
 *      → Load-time: fires once on page load after a short delay so the
 *        page settles visually before the headline animates in.
 *        Pre-hidden via CSS in section-video.liquid (no flash on load).
 *        Replaces the theme's default `data-aos="hero"` animation.
 *
 * Depends on: GSAP 3 + ScrollTrigger from CDN (loaded before this file).
 * Skips: Shopify customiser, prefers-reduced-motion, missing GSAP.
 */

(function () {
  'use strict';

  // Skip in the Shopify customiser — section re-renders and iframe
  // environment cause ScrollTrigger calculations to go wrong.
  if (window.Shopify && window.Shopify.designMode) return;

  // Respect the user's motion preference.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Bail gracefully if the CDN scripts failed to load.
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  // ─── Shared blur-reveal animation settings ────────────────────────────────

  var BLUR_FROM   = 'blur(8px)';
  var BLUR_TO     = 'blur(0px)';
  var OPACITY_FROM = 0.1;
  var DURATION    = 0.75;
  var STAGGER     = 0.055;   // 55ms between words
  var EASE        = 'power2.out';

  // ─── Word splitter ────────────────────────────────────────────────────────

  /**
   * Wrap each word in an element with a <span class="gsap-word">.
   * Uses a TreeWalker so nested inline tags (<em>, <strong>, etc.) are
   * preserved — only bare text nodes are split.
   */
  function splitWords(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    textNodes.forEach(function (textNode) {
      var parts = textNode.nodeValue.split(/(\s+)/);
      var frag  = document.createDocumentFragment();

      parts.forEach(function (part) {
        if (/\S/.test(part)) {
          var span = document.createElement('span');
          span.className    = 'gsap-word';
          span.style.cssText = 'display:inline-block;';
          span.textContent  = part;
          frag.appendChild(span);
        } else {
          frag.appendChild(document.createTextNode(part));
        }
      });

      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  // ─── 1. Rich text section headings (scroll-triggered) ────────────────────

  function initRichTextHeadings() {
    var headings = document.querySelectorAll(
      '.index-rte h1, .index-rte h2, .index-rte h3, .index-rte h4, .index-rte h5, .index-rte h6'
    );

    headings.forEach(function (heading) {
      if (heading.dataset.gsapReady) return;
      heading.dataset.gsapReady = '1';

      splitWords(heading);

      var words = heading.querySelectorAll('.gsap-word');
      if (!words.length) return;

      // Set initial state in JS so headings stay visible if GSAP fails.
      gsap.set(words, {
        opacity:    OPACITY_FROM,
        filter:     BLUR_FROM,
        willChange: 'opacity, filter'
      });

      ScrollTrigger.create({
        trigger: heading,
        start:   'top 90%',
        once:    true,
        onEnter: function () {
          gsap.to(words, {
            opacity:    1,
            filter:     BLUR_TO,
            duration:   DURATION,
            stagger:    STAGGER,
            delay:      0.1,   // brief pause after scroll trigger fires
            ease:       EASE,
            clearProps: 'willChange'
          });
        }
      });
    });
  }

  // ─── 2. Video section hero titles (load-time, replaces AOS heroFadeIn) ───

  function initVideoTitles() {
    var titles = document.querySelectorAll('.gsap-video-title');
    if (!titles.length) return;

    titles.forEach(function (title) {
      if (title.dataset.gsapReady) return;
      title.dataset.gsapReady = '1';

      splitWords(title);

      var words = title.querySelectorAll('.gsap-word');
      if (!words.length) return;

      // The CSS on .gsap-video-title pre-hides the heading at opacity:0.
      // Use direct DOM assignment (not gsap.set) to override it — this is
      // synchronous and guaranteed to win over the class-level style.
      title.style.opacity = '1';

      // gsap.fromTo with immediateRender:true (the default) applies the FROM
      // values synchronously in the same JS tick — so the browser's first
      // paint after this block shows words at 0.1 opacity + blur, not full
      // opacity. No flash between the parent reveal and the words being hidden.
      gsap.fromTo(
        words,
        { opacity: OPACITY_FROM, filter: BLUR_FROM },
        {
          opacity:    1,
          filter:     BLUR_TO,
          duration:   DURATION,
          stagger:    STAGGER,
          delay:      0.5,
          ease:       EASE,
          clearProps: 'filter'
        }
      );
    });
  }

  // ─── Run ──────────────────────────────────────────────────────────────────

  // All scripts are defer'd, so DOMContentLoaded has already fired.
  initRichTextHeadings();
  initVideoTitles();

}());
